import { useEffect, useRef, useState } from 'react';
import loadLeaflet from './loadLeaflet';
import { toPersian } from '../../utils/persianNumber';

const ISFAHAN = { lat: 32.6546, lng: 51.6680 };

// Renders an employee's current route (get_current_map: parallel lat[]/
// lng[] arrays for the path, plus origin/destination points) as a polyline
// with start/end markers on an OpenStreetMap tile layer - CurrentMapPage
// previously just dumped this same JSON as <pre> text with a placeholder
// "map component (OpenLayers/Leaflet)" comment; no map was ever rendered.
export default function LeafletRouteMap({ lat, lng, origin, destination, distance, height = 500 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerGroupRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !containerRef.current) return;
        if (!mapRef.current) {
          const map = L.map(containerRef.current).setView([ISFAHAN.lat, ISFAHAN.lng], 12);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
            maxZoom: 19,
          }).addTo(map);
          mapRef.current = map;
        }
        setReady(true);
        // Leaflet measures the container's size once at init - if this
        // component mounted inside a grid/flex column whose track width
        // wasn't settled yet at that exact instant, the map silently stays
        // 0x0 forever (renders blank) unless told to re-measure. Nudge it
        // on the next couple of frames as a cheap, reliable fix.
        requestAnimationFrame(() => mapRef.current?.invalidateSize());
        setTimeout(() => mapRef.current?.invalidateSize(), 200);
      })
      .catch(() => setFailed(true));

    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const L = window.L;
    if (!ready || !map || !L) return;

    if (layerGroupRef.current) { map.removeLayer(layerGroupRef.current); layerGroupRef.current = null; }

    const points = Array.isArray(lat) && Array.isArray(lng)
      ? lat.map((la, i) => [la, lng[i]]).filter(([la, ln]) => la != null && ln != null)
      : [];
    if (points.length === 0) return;

    const group = L.layerGroup();
    L.polyline(points, { color: '#29AAE1', weight: 4, opacity: 0.8 }).addTo(group);

    if (Array.isArray(origin) && origin.length === 2) {
      L.marker(origin, { title: 'مبدا' })
        .bindPopup('مبدا')
        .addTo(group);
    }
    if (Array.isArray(destination) && destination.length === 2) {
      L.marker(destination, { title: 'مقصد' })
        .bindPopup('مقصد')
        .addTo(group);
    }

    group.addTo(map);
    layerGroupRef.current = group;
    map.invalidateSize();
    map.fitBounds(L.latLngBounds(points), { padding: [30, 30] });
  }, [ready, lat, lng, origin, destination]);

  return (
    <div>
      <div ref={containerRef} style={{ width: '100%', height, borderRadius: 10, overflow: 'hidden', background: '#f8faff' }} />
      {failed && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6, padding: '0 12px' }}>خطا در بارگذاری نقشه - اتصال اینترنت را بررسی کنید</div>}
      {ready && typeof distance === 'number' && (
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, padding: '0 12px' }}>فاصله: {toPersian((distance / 1000).toFixed(2))} کیلومتر</div>
      )}
    </div>
  );
}
